import { useEffect, useState, useRef } from 'react';
import { MapPin, Pencil, Loader2 } from 'lucide-react';
import { usePermissions } from '../../../lib/hooks/usePermissions';
import { getCurrentUserOrganizationId } from '../../../lib/stores/referenceDataCache';
import { ManualOrderService } from '../../../lib/services/manualOrderService';
import { useManualOrderStore } from '../../../lib/stores/manualOrderStore';
import { friendlyDbError } from '../../../lib/utils/friendlyDbError';
import type { ManualOrder } from '../../../lib/types/orders';

/**
 * Riga «Tracking» della sezione Spedizione di un ordine manuale: corriere e
 * numero, modificabili in qualsiasi momento (anche prima della preparazione) da
 * chi gestisce la spedizione.
 *
 * Sempre facoltativo: nessun passaggio lo richiede. Lo stesso valore compare in
 * preparazione (precompilato nella chiusura). Salvataggio senza ricaricare
 * tutto il dettaglio. Negli ordini annullati resta in sola lettura.
 */

interface OrderTrackingSectionProps {
  order: ManualOrder;
  organizationId: string;
  userId: string;
}

export function OrderTrackingSection({ order, organizationId, userId }: OrderTrackingSectionProps) {
  const {canEditInventory} = usePermissions();
  const context = `${organizationId}:${order.id}`;
  const currentContext = useRef(context); currentContext.current=context;
  const [editing, setEditing] = useState(false);
  const [carrier, setCarrier] = useState(order.carrier ?? '');
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ordine ricaricato (es. dopo la chiusura della preparazione): valori aggiornati
  useEffect(() => {
    if (editing) return;
    setCarrier(order.carrier ?? '');
    setTrackingNumber(order.tracking_number ?? '');
  }, [order.carrier, order.tracking_number, editing]);

  useEffect(()=>{setEditing(false);setError(null);setCarrier(order.carrier??'');setTrackingNumber(order.tracking_number??'');},[context]);
  const modificabile = canEditInventory && order.status !== 'cancelled';
  const haTracking = Boolean(order.tracking_number || order.carrier);

  const annulla = () => {
    setCarrier(order.carrier ?? '');
    setTrackingNumber(order.tracking_number ?? '');
    setError(null);
    setEditing(false);
  };

  const salva = async () => {
    if (!userId || !modificabile || saving) return;
    setSaving(true);
    setError(null);
    try {
      if(await getCurrentUserOrganizationId()!==organizationId) throw new Error('Organizzazione cambiata');
      const updated = await ManualOrderService.updateOrder(
        order.id,
        {
          tracking_number: trackingNumber.trim() || null,
          carrier: carrier.trim() || null,
        },
        { organizationId, userId },
      );
      if(currentContext.current!==context || await getCurrentUserOrganizationId()!==organizationId) return;
      useManualOrderStore.setState({ currentOrder: updated });
      setEditing(false);
    } catch (err) {
      setError(friendlyDbError(err) || 'Salvataggio del tracking non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full px-2.5 py-1.5 text-sm border border-[rgba(55,53,47,0.16)] dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-[#37352F] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="py-2.5">
      <div className="flex items-start gap-3">
        <div className="w-24 shrink-0 flex items-center gap-1.5 text-xs text-[#787774] dark:text-gray-400 pt-0.5">
          <MapPin className="w-3.5 h-3.5" />
          Tracking
        </div>
        <div className="flex-1 min-w-0 text-sm">
          {haTracking ? (
            <span className="text-[#37352F] dark:text-white break-all">
              {order.carrier && <span className="font-medium">{order.carrier}</span>}
              {order.carrier && order.tracking_number && ' · '}
              {order.tracking_number}
            </span>
          ) : (
            <span className="text-[#9B9A97] dark:text-gray-500">Non inserito</span>
          )}
        </div>
        {modificabile && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 -my-0.5 text-xs text-[#787774] hover:text-[#37352F] dark:text-gray-400 dark:hover:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-800 rounded transition-colors"
          >
            <Pencil className="w-3 h-3" />
            {haTracking ? 'Modifica' : 'Aggiungi'}
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-2 rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#787774] dark:text-gray-400 mb-1">Corriere</label>
              <input
                type="text"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                placeholder="Es: BRT, GLS, SDA"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs text-[#787774] dark:text-gray-400 mb-1">Numero di tracking</label>
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') salva(); if (e.key === 'Escape') annulla(); }}
                placeholder="Es: 1234567890"
                autoFocus
                className={inputClass}
              />
            </div>
          </div>
          <p className="text-xs text-[#787774] dark:text-gray-400">
            Facoltativo, si può inserire anche prima della preparazione: gli operatori lo vedono mentre preparano l'ordine.
          </p>
          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={annulla}
              disabled={saving}
              className="px-3 py-1.5 text-xs text-[#37352F] dark:text-gray-300 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 transition-colors"
            >
              Annulla
            </button>
            <button
              onClick={salva}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-[#37352F] rounded hover:bg-[#2f2d27] disabled:opacity-60 transition-colors"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              Salva
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderTrackingSection;
